import "components/DocumentStatusTabs/documentStatusTabs.css";
import ThemedSelect from "components/FormControls/ThemedSelect";
import { ClearableSearchInput } from "components/SearchField/SearchField";
import {
  LuCoins,
  LuPlus,
  LuEllipsis,
  LuEye,
  LuMail,
  LuDownload,
  LuPause,
  LuSearch,
  LuRefreshCw,
  LuChevronLeft,
  LuChevronRight,
} from "react-icons/lu";
import {
  availableAgreementContacts,
  agreementRegisterState,
  agreementRegisterLabel,
  agreementMatchesGroup,
  agreementRegisterSummary,
} from "utils/rctiAgreementRegister";
import "./rctiAgreementsRegister.css";
import { formatAbn } from "utils/abn";
import { rctiTimestamp } from "utils/rctiTimestamp";
import { isSignedAgreement } from "utils/rctiAgreementSigned";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useMatch, useNavigate, useSearchParams } from "react-router-dom";
import { DashboardNavigation } from "layouts/businessUserDashboard";
import { useMaterialUIController } from "context";
import AppHeader from "components/AppHeader";
import { CardSkeleton, Skeleton, TableSkeleton } from "components/Skeleton";
import PageSkeleton from "components/Skeleton/PageSkeleton";
import { showDraftSavedMessage } from "components/InlineFeedback/DraftSaveConfirmation";
import { agreementRequest } from "utils/rctiAgreementAPI";
import "./agreements.css";
import "./rctiResponsive.css";
import AgreementReview from "./AgreementReview";
import { agreementReference } from "./RctiDesignSystem";
import { AgreementOverview, AgreementVerification } from "./AgreementJourney";
import AgreementEditor from "./AgreementEditor";
import InvoiceCustomerDialog from "layouts/Invoices/InvoiceCustomerDialog";
import { savePdfResponse } from "utils/documentPdf";
import ListingActionMenu from "components/ListingActionMenu";

export default function RctiAgreements() {
  const [controller] = useMaterialUIController();
  const [navOpen, setNavOpen] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const detailMatch = useMatch("/rcti-agreements/:id");
  const isNewAgreement = Boolean(useMatch("/rcti-agreements/new/*"));
  const routeID = isNewAgreement ? "" : detailMatch?.params.id || "";
  const [data, setData] = useState(null);
  const [wizardStage, setWizardStage] = useState("prepare");
  const stage = isNewAgreement ? wizardStage : "register";
  const setStage = useCallback(
    (next) => {
      if (next === "register") navigate("/rcti-agreements");
      else {
        setWizardStage(next);
        if (!isNewAgreement) navigate("/rcti-agreements/new");
      }
    },
    [isNewAgreement, navigate]
  );
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [scopeFilter, setScopeFilter] = useState("");
  const [page, setPage] = useState(1);
  const hasActiveFilters = Boolean(search || filter !== "all" || statusFilter !== "all" || scopeFilter);
  const clearFilters = () => {
    setSearch("");
    setFilter("all");
    setStatusFilter("all");
    setScopeFilter("");
    setPage(1);
  };
  const [openActionMenu, setOpenActionMenu] = useState(null);
  useEffect(() => {
    setPage(1);
  }, [filter, statusFilter, scopeFilter, search]);
  const [linkedRecords, setLinkedRecords] = useState(null);
  const [linkedError, setLinkedError] = useState("");
  const [reviewed, setReviewed] = useState(false);
  const [preparedAt, setPreparedAt] = useState("");
  const [contactID, setContactID] = useState("");
  const [createContactOpen, setCreateContactOpen] = useState(false);
  const returnedContactID = params.get("supplier") || "";
  const [scope, setScope] = useState("");
  let draftOwner = {};
  try {
    draftOwner = JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "{}");
  } catch (_) {}
  const draftKey = `RCTI_AGREEMENT_DRAFT:${
    draftOwner.businessUserID || draftOwner._id || "unknown"
  }:${draftOwner._id || "unknown"}`;
  const resumeSetup = params.get("resume") === "draft";
  const [savedSetup, setSavedSetup] = useState(null);
  const [buyerRepresentative, setBuyerRepresentative] = useState({
    name: "",
    position: "",
  });
  const [supplierRepresentative, setSupplierRepresentative] = useState({
    name: "",
    position: "",
  });
  const [gstConsent, setGstConsent] = useState(false);
  useEffect(() => {
    let draft = null;
    try {
      draft = JSON.parse(sessionStorage.getItem(draftKey) || "null");
    } catch (_) {
      /* Ignore an unavailable or outdated tab-local draft. */
    }
    setSavedSetup(draft);
    if (!isNewAgreement) return;
    // Restoring a supplier requires an explicit contact return or Resume action.
    const restoredContactID =
      returnedContactID || (resumeSetup ? draft?.contactID || "" : "");
    setContactID(restoredContactID);
    setScope(draft?.scope || "");
    setBuyerRepresentative(
      draft?.buyerRepresentative || { name: "", position: "" }
    );
    setSupplierRepresentative(
      restoredContactID && restoredContactID === draft?.contactID
        ? draft.supplierRepresentative || { name: "", position: "" }
        : { name: "", position: "" }
    );
    setSignature("");
    setConsent(false);
    setGstConsent(false);
    setReviewed(false);
    setPreparedAt("");
    setWizardStage("prepare");
  }, [draftKey, returnedContactID, isNewAgreement, resumeSetup]);
  const manageContact = (id) => {
    if (!id) { setCreateContactOpen(true); return; }
    try {
      sessionStorage.setItem(
        draftKey,
        JSON.stringify({
          savedAt: new Date().toISOString(),
          contactID,
          scope,
          buyerRepresentative,
          supplierRepresentative,
        })
      );
      navigate(
        id
          ? `/contacts/editContact/${encodeURIComponent(
              id
            )}?returnTo=%2Frcti-agreements%2Fnew`
          : "/contacts/addContact?returnTo=%2Frcti-agreements%2Fnew",
        {
          state: { rctiAgreementReturn: true },
        }
      );
    } catch (_) {
      setError(
        "The agreement draft could not be saved. Keep this page open and try again."
      );
    }
  };
  const saveDraft = () => {
    try {
      sessionStorage.setItem(
        draftKey,
        JSON.stringify({
          savedAt: new Date().toISOString(),
          contactID,
          scope,
          buyerRepresentative,
          supplierRepresentative,
        })
      );
      setSignature("");
      setConsent(false);
      setGstConsent(false);
      setMessage(
        "Draft saved in this tab. Use Resume saved draft under Saved agreement setup on the register."
      );
      setStage("register");
    } catch (_) {
      setError("Draft could not be saved. Keep this page open and try again.");
    }
  };
  const [signature, setSignature] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const legacyID = params.get("id") || "";
  const expanded = routeID || legacyID;
  const setExpanded = useCallback(
    (id) =>
      navigate(
        id ? `/rcti-agreements/${encodeURIComponent(id)}` : "/rcti-agreements"
      ),
    [navigate]
  );
  useEffect(() => {
    if (legacyID)
      navigate(`/rcti-agreements/${encodeURIComponent(routeID || legacyID)}`, {
        replace: true,
      });
  }, [legacyID, routeID, navigate]);
  const load = useCallback(async () => {
    const output = await agreementRequest("", undefined, "get");
    setData(output);
  }, []);
  useEffect(() => {
    agreementRequest("/notifications/read").catch(() => {});
    load().catch((e) => setError(e.message));
    const timer = setInterval(
      () =>
        agreementRequest("", undefined, "get")
          .then(setData)
          .catch(() => {}),
      60000
    );
    return () => clearInterval(timer);
  }, [load]);
  const run = async (action) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const result = await action();
      if (result?.sent === true) showDraftSavedMessage("Email sent successfully");
      setMessage(
        result?.message ||
          (result?.sent === false
            ? "Email delivery failed. Please retry."
            : "Saved successfully.")
      );
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    let current = true;
    setLinkedRecords(null);
    setLinkedError("");
    if (expanded)
      agreementRequest(
        `/${encodeURIComponent(expanded)}/rctis`,
        undefined,
        "get"
      )
        .then((rows) => {
          if (current) setLinkedRecords(Array.isArray(rows) ? rows : []);
        })
        .catch((error) => {
          if (current) setLinkedError(error.message);
        });
    return () => {
      current = false;
    };
  }, [expanded]);
  const availableContacts = availableAgreementContacts(
    data?.contacts,
    data?.agreements
  );
  const selected = availableContacts.find((c) => c._id === contactID);
  useEffect(() => {
    if (
      isNewAgreement &&
      contactID &&
      (data?.contacts.some(
        (c) => c._id === contactID && c.hasActiveRctiAgreement === true
      ) ||
        data?.agreements.some(
          (a) =>
            a.status === "active" &&
            String(a.contactID?._id || a.contactID) === String(contactID)
        ))
    ) {
      setContactID("");
      setSignature("");
      setConsent(false);
      setGstConsent(false);
      setReviewed(false);
      setWizardStage("prepare");
      setMessage(
        "This supplier already has an active RCTI agreement. Select another supplier or open the existing agreement from the register."
      );
    }
  }, [isNewAgreement, contactID, data]);
  const openAgreement = data?.agreements.find((a) => a._id === expanded);
  const stages = ["select", "verify", "prepare", "review"];
  const step = stages.indexOf(stage);
  const agreements = [...(data?.agreements || [])].sort((first, second) => {
    const latest = (row) => Math.max(0, ...[row.updatedAt, row.createdAt].map((value) => Date.parse(value) || 0));
    return latest(second) - latest(first) || String(second._id).localeCompare(String(first._id));
  });
  const stats = agreementRegisterSummary(agreements);
  const scopes = [
    ...new Set(agreements.map((a) => a.scope).filter(Boolean)),
  ].sort();
  const visible = agreements.filter(
    (a) =>
      agreementMatchesGroup(a, filter) &&
      agreementMatchesGroup(a, statusFilter) &&
      (!scopeFilter || a.scope === scopeFilter) &&
      `${a.supplier.name} ${agreementReference(a._id)} ${a.scope}`
        .toLowerCase()
        .includes(search.trim().toLowerCase())
  );
  const currentPage = Math.min(
    page,
    Math.max(1, Math.ceil(visible.length / 10))
  );
  const pageRows = visible.slice((currentPage - 1) * 10, currentPage * 10);
  const shortDate = (value) =>
    value && Number.isFinite(new Date(value).getTime())
      ? new Date(value).toLocaleDateString("en-AU", {
          day: "numeric",
          month: "short",
          year: "numeric",
          timeZone: "UTC",
        })
      : "Not recorded";
  const registryDetail = (a) => {
    const state = agreementRegisterState(a);
    if (state === "active")
      return `Signed ${rctiTimestamp(a.supplierSignedAt)}`;
    if (state === "awaiting")
      return a.invitationExpiresAt
        ? `Sign by ${rctiTimestamp(a.invitationExpiresAt)}`
        : "Invitation sent";
    if (state === "proposed")
      return a.invitationDelivery === "failed"
        ? "Invitation failed"
        : "Not sent";
    return state === "expired"
      ? "Unsigned invitation ended"
      : a.events?.length
      ? rctiTimestamp(a.events[a.events.length - 1].at)
      : "Not available for issue";
  };
  const preview =
    data && selected
      ? {
          _id: "",
          preparedAt,
          buyer: data.buyer,
          supplier: {
            name: selected.companyName || selected.name,
            abnNumber: selected.abnNumber,
            emailAddress: selected.emailAddress,
          },
          scope,
          terms: data.terms,
          version: data.version,
          status: "proposed",
          buyerSignature: signature,
          buyerRepresentative,
          supplierRepresentative,
        }
      : null;
  const send = (invitationEmail) =>
    run(async () => {
      const result = await agreementRequest("", {
        invitationEmail,
        contactID,
        scope,
        signature,
        consent,
        buyerRepresentative,
        supplierRepresentative,
        gstConsent,
      });
      // A failed SMTP attempt can still have created the proposal. Show that
      // saved record so retry uses Resend rather than creating a duplicate.
      if (result._id || result.sent) {
        sessionStorage.removeItem(draftKey);
        setSavedSetup(null);
        if (result._id) setExpanded(String(result._id));
        setSignature("");
        setConsent(false);
        setReviewed(false);
        if (!result._id) setStage("register");
      }
      return result;
    });
  const download = (a) =>
    run(async () => {
      const result = await agreementRequest(`/${a._id}/pdf`);
      savePdfResponse({ data: result.pdf }, result.filename);
      return { message: "Agreement PDF downloaded." };
    });
  const actions = (a) => (
    <div className="rcti-agreements__actions">
      {a.status === "active" && (
        <>
          <button
            className="rcti-button-secondary"
            disabled={busy || !data.canWrite}
            onClick={() =>
              run(() => agreementRequest(`/${a._id}/supplier-email`))
            }
          >
            Email supplier suspension link
          </button>
        </>
      )}
      {isSignedAgreement(a) && (
        <button
          className="rcti-button-secondary"
          disabled={busy}
          onClick={() => download(a)}
        >
          Download agreement PDF
        </button>
      )}
      {["active", "proposed", "pending"].includes(a.status) && (
        <button
          className="rcti-button-danger"
          disabled={busy || !data.canWrite}
          onClick={() => {
            if (
              window.confirm(
                "Suspend this agreement? A new signed agreement will be needed to reactivate it."
              )
            )
              run(() => agreementRequest(`/${a._id}/suspend`));
          }}
        >
          Suspend agreement
        </button>
      )}
    </div>
  );
  const closeActionMenu = () => setOpenActionMenu(null);
  const renderRegisterActions = (agreement, state) => {
    const menuOpen = openActionMenu?.id === agreement._id;
    const path = `/rcti-agreements/${encodeURIComponent(agreement._id)}`;
    const canManage = Boolean(data?.canWrite);

    return (
      <div className="rcti-agreement-register-actions">
        <button
          type="button"
          className="rcti-agreement-dots-button"
          aria-label={`Actions for ${agreement.supplier.name}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={(event) =>
            setOpenActionMenu(
              menuOpen
                ? null
                : { id: agreement._id, anchor: event.currentTarget }
            )
          }
        >
          <LuEllipsis aria-hidden="true" />
        </button>
        {menuOpen && (
          <ListingActionMenu
            anchorEl={openActionMenu.anchor}
            label={`Actions for ${agreement.supplier.name}`}
            onClose={closeActionMenu}
          >
            <Link
              className="rcti-agreement-menu-item"
              to={path}
              onClick={closeActionMenu}
            >
              <LuEye aria-hidden="true" />
              <span>{state === "proposed" ? "Resume" : "View"}</span>
            </Link>
            {canManage && agreement.status === "active" && (
              <Link
                className="rcti-agreement-menu-item"
                to="/rcti/add"
                state={{ rctiSupplierID: agreement.contactID?._id || agreement.contactID }}
                onClick={closeActionMenu}
              >
                <LuPlus aria-hidden="true" />
                <span>Create RCTI</span>
              </Link>
            )}
            {canManage && ["proposed", "pending"].includes(agreement.status) && (
              <button
                type="button"
                className="rcti-agreement-menu-item"
                disabled={busy}
                onClick={() => {
                  closeActionMenu();
                  run(() => agreementRequest(`/${agreement._id}/resend`));
                }}
              >
                <LuRefreshCw aria-hidden="true" />
                <span>Resend invitation</span>
              </button>
            )}
            {canManage && agreement.status === "active" && (
              <button
                type="button"
                className="rcti-agreement-menu-item"
                disabled={busy}
                onClick={() => {
                  closeActionMenu();
                  run(() =>
                    agreementRequest(`/${agreement._id}/supplier-email`)
                  );
                }}
              >
                <LuMail aria-hidden="true" />
                <span>Email suspension link</span>
              </button>
            )}
            {isSignedAgreement(agreement) && (
              <button
                type="button"
                className="rcti-agreement-menu-item"
                disabled={busy}
                onClick={() => {
                  closeActionMenu();
                  download(agreement);
                }}
              >
                <LuDownload aria-hidden="true" />
                <span>Download agreement PDF</span>
              </button>
            )}
            {canManage &&
              ["active", "proposed", "pending"].includes(agreement.status) && (
                <button
                  type="button"
                  className="rcti-agreement-menu-item danger"
                  disabled={busy}
                  onClick={() => {
                    closeActionMenu();
                    if (
                      window.confirm(
                        "Suspend this agreement? A new signed agreement will be needed to reactivate it."
                      )
                    ) {
                      run(() =>
                        agreementRequest(`/${agreement._id}/suspend`)
                      );
                    }
                  }}
                >
                  <LuPause aria-hidden="true" />
                  <span>Suspend agreement</span>
                </button>
              )}
          </ListingActionMenu>
        )}
      </div>
    );
  };
  return (
    <div
      className="business-dashboard rcti-agreements rcti-designed"
      data-theme={controller.darkMode ? "dark" : "light"}
    >
      <DashboardNavigation
        open={navOpen}
        onClose={() => setNavOpen(false)}
        activePath="/rcti-agreements"
      />
      <main className="dashboard-main rcti-agreements__main">
        <AppHeader
          searchPlaceholder="Search RCTIs, suppliers, contacts"
          onOpenNav={() => setNavOpen(true)}
        />
        <div
          className={`rcti-agreements__content${
            expanded || ["select", "verify", "review"].includes(stage)
              ? " agreement-journey-page"
              : ""
          }${
            stage === "review"
              ? " agreement-review-page"
              : stage === "prepare"
              ? " agreement-editor-page"
              : stage === "register" && !expanded
              ? " rcti-c04-register-page"
              : ""
          }`}
        >
          {!expanded && step > 0 && (
            <button
              className="rcti-design-back"
              onClick={() => setStage(stages[step - 1])}
            >
              ←{" "}
              {stage === "review"
                ? "Edit agreement"
                : stage === "prepare"
                ? "Back to verification"
                : stage === "verify"
                ? "Back to supplier"
                : "Back"}
            </button>
          )}
          <header className="rcti-design-heading">
            <div>
              <p className="rcti-design-breadcrumb">
                Buy / <Link to="/rcti">RCTIs</Link> /{" "}
                <Link to="/rcti-agreements">Agreements</Link>
                {openAgreement && (
                  <> / {agreementReference(openAgreement._id)}</>
                )}
              </p>
              <h1>
                {openAgreement
                  ? `Agreement ${agreementReference(openAgreement._id)}`
                  : {
                      register: "Agreements",
                      select: "Select or create supplier",
                      verify: "Verify buyer and supplier",
                      prepare: "Create onboarding agreement",
                      review: "Review agreement and invitation",
                    }[stage]}
              </h1>
              <p>
                {openAgreement
                  ? `Recipient created tax invoice agreement · ${openAgreement.buyer.name} & ${openAgreement.supplier.name}`
                  : stage === "register"
                  ? `Manage standalone supplier agreements for ${
                      data?.buyer.name || "your business"
                    }’s RCTIs.`
                  : stage === "review"
                  ? "Check the proposed contract and the email your supplier will receive."
                  : stage === "verify"
                  ? "Confirm legal identities and eligibility before creating a standalone agreement."
                  : stage === "prepare"
                  ? `Prepare the standalone RCTI contract${
                      selected
                        ? ` for ${selected.companyName || selected.name}`
                        : ""
                    }. No tax document is created.`
                  : "Prepare the agreement, confirm eligibility and invite your supplier to sign."}
              </p>
            </div>
            {stage === "prepare" && (
              <button
                type="button"
                className="rcti-button-secondary"
                onClick={saveDraft}
                disabled={busy}
              >
                Save &amp; leave
              </button>
            )}
            {stage === "register" && !expanded && (
              <button
                disabled={!data?.canWrite}
                onClick={() => setStage("prepare")}
              >
                New agreement
              </button>
            )}
          </header>
          {stage === "register" && !expanded && savedSetup && (
            <section
              className="rcti-agreement-card agreement-saved-setup"
              aria-label="Saved agreement setup"
            >
              <div>
                <h2>Saved agreement setup</h2>
                <p>
                  {data?.contacts.find(
                    (contact) => contact._id === savedSetup.contactID
                  )?.companyName ||
                    data?.contacts.find(
                      (contact) => contact._id === savedSetup.contactID
                    )?.name ||
                    "Supplier not selected"}{" "}
                  · Not sent
                </p>
                <p>{savedSetup.scope || "Scope not entered yet"}</p>
                <small>
                  Saved in this browser tab
                  {savedSetup.savedAt
                    ? ` · ${rctiTimestamp(savedSetup.savedAt)}`
                    : ""}
                  . This is an unfinished setup, not an active or sent
                  agreement. Signatures and consent are not saved.
                </small>
              </div>
              {data?.canWrite && (
                <Link
                  className="agreement-resume-setup"
                  to="/rcti-agreements/new?resume=draft"
                >
                  Resume saved draft
                </Link>
              )}
            </section>
          )}

          {error && (
            <p role="alert" className="rcti-agreements__error">
              {error}
            </p>
          )}
          {!data && error && (
            <button
              type="button"
              className="rcti-button-secondary"
              disabled={busy}
              onClick={() => run(load)}
            >
              Retry loading agreements
            </button>
          )}
          {message && (
            <p role="status" className="rcti-design-notice">
              {message}
            </p>
          )}
          {!data && !error && (
            expanded || isNewAgreement ? <PageSkeleton type={expanded ? "detail" : "editor"} variant="document" label="Loading RCTI agreement…" /> :
            <div
              className="rcti-agreements__loading"
              role="status"
              aria-busy="true"
              aria-label="Loading RCTI agreements"
            >
              <span className="skeleton-sr-only">Loading RCTI agreements…</span>
              <section className="rcti-design-metrics" aria-hidden="true">
                <CardSkeleton variant="stat" count={3} />
              </section>
              <div
                className="rcti-agreements__loading-controls"
                aria-hidden="true"
              >
                <Skeleton width="min(360px, 100%)" height={40} radius={10} />
                <Skeleton width={180} height={40} radius={10} />
              </div>
              <TableSkeleton
                variant="div"
                columns={7}
                rows={6}
                hasHeader
                hasActions
              />
            </div>
          )}
          {data && (
            <>
              {openAgreement ? (
                <>
                  <AgreementOverview
                    agreement={openAgreement}
                    actions={actions(openAgreement)}
                    primaryAction={
                      ["proposed", "pending"].includes(openAgreement.status) ? (
                        <button
                          className="agreement-overview-primary"
                          disabled={busy || !data.canWrite}
                          onClick={() =>
                            run(() =>
                              agreementRequest(`/${openAgreement._id}/resend`)
                            )
                          }
                        >
                          Resend invitation
                        </button>
                      ) : null
                    }
                  >
                    <section className="rcti-agreement-card">
                      <h2>Linked RCTIs</h2>
                      {linkedError ? (
                        <p role="alert">
                          Linked documents could not be loaded.{" "}
                          <Link to="/rcti">Open the RCTI register</Link>
                        </p>
                      ) : linkedRecords === null ? (
                        <p role="status">Loading linked documents…</p>
                      ) : linkedRecords.length ? (
                        <ul className="agreement-linked-rctis">
                          {linkedRecords.map((record) => (
                            <li key={record._id}>
                              <strong>
                                {record.rctiNumber || "Draft"} · v
                                {record.version || 1}
                              </strong>
                              <p>
                                {record.currencyValue || "AUD"}{" "}
                                {Number(record.finalAmount || 0).toFixed(2)}
                              </p>
                              <small>
                                {record.issuedAt || Number(record.emailSent) > 0
                                  ? "Issued · Locked"
                                  : "Draft · Not payable"}
                              </small>
                              <Link
                                to={
                                  record.issuedAt ||
                                  Number(record.emailSent) > 0
                                    ? `/rcti/view/${record._id}`
                                    : `/rcti/edit/${record._id}`
                                }
                              >
                                {record.issuedAt || Number(record.emailSent) > 0
                                  ? "View RCTI"
                                  : "Resume draft"}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p>No RCTIs linked to this agreement.</p>
                      )}
                      <Link to="/rcti">View RCTI register</Link>
                      {linkedRecords?.length === 50 && (
                        <small>Showing the 50 most recent documents.</small>
                      )}
                    </section>
                  </AgreementOverview>
                </>
              ) : expanded ? (
                <section
                  className="rcti-agreement-card"
                  role={busy ? "status" : "alert"}
                >
                  <h2>
                    {busy ? "Loading agreement…" : "Agreement unavailable"}
                  </h2>
                  {!busy && (
                    <>
                      <p>
                        This agreement is unavailable or you do not have
                        permission to view it.
                      </p>
                      <Link to="/rcti-agreements">Back to agreements</Link>
                    </>
                  )}
                </section>
              ) : stage === "register" ? (
                <>
                  <section
                    className="rcti-design-metrics"
                    aria-label="Agreement summary"
                  >
                    {[
                      [
                        "Active agreements",
                        "active",
                        stats.active,
                        "Eligible for covered supplies",
                      ],
                      [
                        "In progress",
                        "progress",
                        stats.progress,
                        `${
                          agreements.filter(
                            (a) => agreementRegisterState(a) === "proposed"
                          ).length
                        } proposed · ${
                          agreements.filter(
                            (a) => agreementRegisterState(a) === "awaiting"
                          ).length
                        } awaiting signature`,
                      ],
                      [
                        "Not available for issue",
                        "unavailable",
                        stats.unavailable,
                        `${
                          agreements.filter(
                            (a) => agreementRegisterState(a) === "suspended"
                          ).length
                        } suspended · ${
                          agreements.filter((a) =>
                            ["ended", "expired"].includes(
                              agreementRegisterState(a)
                            )
                          ).length
                        } ended / expired`,
                      ],
                    ].map(([label, value, count, description]) => (
                      <button
                        key={value}
                        type="button"
                        className="rcti-agreement-card"
                        aria-pressed={filter === value}
                        onClick={() => {
                          setFilter(value);
                          setStatusFilter("all");
                        }}
                      >
                        <span>
                          {label}
                          <LuCoins aria-hidden="true" />
                        </span>
                        <strong>{count}</strong>
                        <small>{description}</small>
                      </button>
                    ))}
                  </section>
                  <div
                    className="rcti-design-tabs document-status-tabs"
                    role="tablist"
                    aria-label="Agreement status"
                  >
                    {[
                      [`All (${agreements.length})`, "all"],
                      [`Active (${stats.active})`, "active"],
                      [`In progress (${stats.progress})`, "progress"],
                      ["Suspended", "suspended"],
                      ["Ended / expired", "unavailable"],
                    ].map(([label, value]) => (
                      <button
                        role="tab"
                        aria-selected={filter === value}
                        className={filter === value ? "is-active" : ""}
                        key={value}
                        onClick={() => {
                          setFilter(value);
                          setStatusFilter("all");
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="rcti-design-filter">
                    <label className="rcti-c04-search">
                      <LuSearch aria-hidden="true" />
                      <ClearableSearchInput
                        id="agreement-register-search"
                        aria-label="Search agreements"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search supplier or agreement reference"
                      />
                    </label>
                    <ThemedSelect
                      dark={controller.darkMode}
                      aria-label="Agreement status filter"
                      value={statusFilter}
                      displayValue={
                        statusFilter === "all"
                          ? "Status"
                          : agreementRegisterLabel(statusFilter)
                      }
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">All</option>
                      {[
                        "proposed",
                        "awaiting",
                        "active",
                        "suspended",
                        "ended",
                        "expired",
                      ].map((state) => (
                        <option key={state} value={state}>
                          {agreementRegisterLabel(state)}
                        </option>
                      ))}
                    </ThemedSelect>
                    <ThemedSelect
                      dark={controller.darkMode}
                      aria-label="Supply scope filter"
                      value={scopeFilter}
                      displayValue={
                        scopeFilter || "Scope"
                      }
                      onChange={(e) => setScopeFilter(e.target.value)}
                    >
                      <option value="">All</option>
                      {scopes.map((scope) => (
                        <option key={scope} value={scope}>
                          {scope}
                        </option>
                      ))}
                    </ThemedSelect>
                    {hasActiveFilters && (
                      <button type="button" className="rcti-c04-clear-filters" onClick={clearFilters}>
                        Clear filters
                      </button>
                    )}
                  </div>
                  <section className="rcti-design-register">
                    <header className="rcti-c04-table-heading">
                      <h2>{visible.length} supplier agreements</h2>
                      <span>
                        Buyer: {data.buyer.name} · As of {shortDate(new Date())}
                      </span>
                    </header>
                    <div className="rcti-design-table-scroll">
                      <table>
                        <thead>
                          <tr>
                            <th>Supplier / reference</th>
                            <th>Covered taxable supplies</th>
                            <th>Wording</th>
                            <th>Effective</th>
                            <th>Duration</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pageRows.map((a) => {
                            const state = agreementRegisterState(a);
                            return (
                              <tr key={a._id}>
                                <td>
                                  <strong>{a.supplier.name}</strong>
                                  <Link
                                    to={`/rcti-agreements/${encodeURIComponent(
                                      a._id
                                    )}`}
                                  >
                                    {agreementReference(a._id)}
                                  </Link>
                                </td>
                                <td>{a.scope}</td>
                                <td>v{a.version}</td>
                                <td>
                                  {a.supplierSignedAt ? (
                                    <time
                                      dateTime={a.supplierSignedAt}
                                      title={rctiTimestamp(a.supplierSignedAt)}
                                    >
                                      {shortDate(a.supplierSignedAt)}
                                    </time>
                                  ) : (
                                    "On acceptance"
                                  )}
                                </td>
                                <td>
                                  {state === "expired"
                                    ? "Signing window ended"
                                    : "No fixed expiry"}
                                </td>
                                <td>
                                  <span
                                    className={`rcti-agreement-badge rcti-agreement-badge--${state}`}
                                  >
                                    {agreementRegisterLabel(state)}
                                  </span>
                                  <small>{registryDetail(a)}</small>
                                </td>
                                <td className="rcti-agreement-actions-cell">
                                  {renderRegisterActions(a, state)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {!visible.length && (
                      <div className="rcti-design-empty">
                        <h3>
                          {search ||
                          filter !== "all" ||
                          statusFilter !== "all" ||
                          scopeFilter
                            ? "No matching agreements"
                            : "Set up your first RCTI agreement"}
                        </h3>
                        <p>
                          Invite an eligible supplier to review and sign before
                          you issue an RCTI.
                        </p>
                        {!hasActiveFilters && (
                          <button
                            disabled={!data.canWrite}
                            onClick={() => setStage("prepare")}
                          >
                            Create agreement
                          </button>
                        )}
                      </div>
                    )}
                    <footer className="rcti-c04-pagination">
                      <span>
                        Showing{" "}
                        {visible.length
                          ? `${(currentPage - 1) * 10 + 1}–${Math.min(
                              currentPage * 10,
                              visible.length
                            )}`
                          : "0"}{" "}
                        of {visible.length} agreements
                      </span>
                      <div>
                        <button
                          type="button"
                          className="rcti-button-secondary"
                          aria-label="Previous agreement page"
                          disabled={currentPage <= 1}
                          onClick={() => setPage(currentPage - 1)}
                        >
                          <LuChevronLeft />
                        </button>
                        <button
                          type="button"
                          className="rcti-button-secondary"
                          aria-label="Next agreement page"
                          disabled={currentPage * 10 >= visible.length}
                          onClick={() => setPage(currentPage + 1)}
                        >
                          <LuChevronRight />
                        </button>
                      </div>
                    </footer>
                  </section>
                  <footer className="rcti-c04-footer-note">
                    <p>
                      Suspension or the end of an unsigned invitation stops
                      future issue under that agreement. Existing RCTIs and
                      unpaid balances remain in history. Signed agreements
                      continue while eligible, unless suspended.
                    </p>
                    <Link to="/MyProfile?returnTo=%2Frcti-agreements">
                      Manage business eligibility in profile
                    </Link>
                  </footer>
                </>
              ) : stage === "verify" ? (
                <AgreementVerification
                  data={data}
                  selected={selected}
                  onManageContact={manageContact}
                  onSupplier={() => setStage("select")}
                  onContinue={() => setStage("prepare")}
                />
              ) : stage === "prepare" ? (
                <AgreementEditor
                  data={{ ...data, contacts: availableContacts }}
                  selected={selected}
                  contactID={contactID}
                  onContact={(id) => {
                    if (id === "add-new-contact") { setCreateContactOpen(true); return; }
                    if (id !== contactID)
                      setSupplierRepresentative({ name: "", position: "" });
                    setContactID(id);
                    setSignature("");
                    setConsent(false);
                    setGstConsent(false);
                    setReviewed(false);
                  }}
                  scope={scope}
                  onScope={setScope}
                  buyerRepresentative={buyerRepresentative}
                  onBuyerRepresentative={setBuyerRepresentative}
                  supplierRepresentative={supplierRepresentative}
                  onSupplierRepresentative={setSupplierRepresentative}
                  signature={signature}
                  onSignature={setSignature}
                  gstConsent={gstConsent}
                  onGstConsent={setGstConsent}
                  consent={consent}
                  onConsent={setConsent}
                  busy={busy}
                  onManageContact={manageContact}
                  onVerify={() => setStage(selected ? "verify" : "select")}
                  onSave={saveDraft}
                  onReview={() => {
                    setPreparedAt(new Date().toISOString());
                    setReviewed(false);
                    setStage("review");
                  }}
                />
              ) : stage === "review" && preview ? (
                <AgreementReview
                  agreement={preview}
                  reviewed={reviewed}
                  onReviewed={setReviewed}
                  busy={busy}
                  canWrite={
                    data.canWrite &&
                    selected.reasons.length === 0 &&
                    consent &&
                    gstConsent &&
                    Boolean(
                      buyerRepresentative.name.trim() &&
                        buyerRepresentative.position.trim() &&
                        supplierRepresentative.name.trim() &&
                        supplierRepresentative.position.trim()
                    )
                  }
                  onEdit={() => setStage("prepare")}
                  onSend={send}
                />
              ) : (
                <>
                  <div className="rcti-design-columns">
                    <div className="rcti-design-stack">
                      {stage === "select" && (
                        <section className="rcti-agreement-card">
                          <h2>Choose a supplier</h2>
                          <label>
                            Contact
                            <ThemedSelect
                              dark={controller.darkMode}
                              aria-label="Contact"
                              value={contactID}
                              onChange={(e) => {
                                if (e.target.value === "add-new-contact") { setCreateContactOpen(true); return; }
                                setContactID(e.target.value);
                                setSignature("");
                                setConsent(false);
                                setGstConsent(false);
                                setReviewed(false);
                              }}
                            >
                              <option value="">Select a contact</option>
                              <option value="add-new-contact">Add new contact</option>
                              {availableContacts.map((c) => (
                                <option key={c._id} value={c._id}
                                  data-status={c.reasons?.length ? "Not eligible" : "Eligible"}>
                                  {c.companyName || c.name}
                                </option>
                              ))}
                            </ThemedSelect>
                          </label>
                          {selected && (
                            <div className="rcti-design-notice">
                              <strong>
                                {selected.companyName || selected.name}
                              </strong>
                              <p>
                                ABN{" "}
                                {formatAbn(selected.abnNumber) || "Not added"} ·{" "}
                                {selected.emailAddress || "Email not added"}
                              </p>
                              <button
                                type="button"
                                className="rcti-button-secondary"
                                onClick={() => manageContact(selected._id)}
                              >
                                Edit supplier contact
                              </button>
                            </div>
                          )}
                          {selected && (
                            <section className="agreement-contact-summary">
                              <h3>Supplier contact details</h3>
                              <dl className="agreement-facts">
                                {[
                                  ["Contact type", selected.contactType],
                                  ["Contact status", selected.contactStatus],
                                  [
                                    "Legal / business name",
                                    selected.companyName,
                                  ],
                                  ["ABN", formatAbn(selected.abnNumber)],
                                  ["Primary person", selected.name],
                                  ["Email", selected.emailAddress],
                                  ["Mobile", selected.phoneNumber],
                                  [
                                    "Business address",
                                    [
                                      selected.streetAddress,
                                      selected.city,
                                      selected.state,
                                      selected.zipCode,
                                    ]
                                      .filter(Boolean)
                                      .join(", "),
                                  ],
                                ].map(([label, value]) => (
                                  <React.Fragment key={label}>
                                    <dt>{label}</dt>
                                    <dd>{value || "Not recorded"}</dd>
                                  </React.Fragment>
                                ))}
                              </dl>
                              <p>
                                Edit the existing contact to update these
                                details. Continuing uses this contact, without
                                creating a duplicate.
                              </p>
                            </section>
                          )}
                          <p>
                            Not in your contacts?{" "}
                            <button
                              type="button"
                              className="rcti-button-secondary"
                              onClick={() => manageContact()}
                            >
                              Create new contact
                            </button>
                          </p>
                        </section>
                      )}
                    </div>
                    <aside className="rcti-design-sidebar">
                      <section className="rcti-agreement-card">
                        <h2>Who is issuing?</h2>
                        <strong>{data.buyer.name}</strong>
                        <p>
                          Buyer / issuer · ABN {formatAbn(data.buyer.abnNumber)}
                        </p>
                        <p>
                          The buyer determines the value of the supply and
                          issues the RCTI. The supplier reviews and signs the
                          agreement.
                        </p>
                      </section>
                      <section className="rcti-agreement-card">
                        <h2>What happens next?</h2>
                        <p>
                          Sending invites the supplier to review and sign. The
                          agreement becomes active only after acceptance.
                        </p>
                        <hr />
                        <p>
                          No tax invoice or payable is created by this action.
                        </p>
                        <p>
                          Both parties receive separate suspension links after
                          activation.
                        </p>
                      </section>
                    </aside>
                  </div>
                  <footer className="rcti-design-footer">
                    <button
                      className="rcti-button-secondary"
                      disabled={busy}
                      onClick={() => setStage("register")}
                    >
                      ← Cancel
                    </button>
                    <button
                      disabled={busy || !data.canWrite || !selected}
                      onClick={() => setStage("verify")}
                    >
                      Continue to verification
                    </button>
                  </footer>
                </>
              )}
            </>
          )}
        </div>
      </main>
      {createContactOpen && (
        <InvoiceCustomerDialog action="create" rctiEligibility
          businessUserID={draftOwner.businessUserID || draftOwner._id}
          darkMode={controller.darkMode}
          onClose={() => setCreateContactOpen(false)}
          onCreated={async (contact) => {
            setSignature(""); setConsent(false); setGstConsent(false); setReviewed(false);
            setSupplierRepresentative({ name: "", position: "" });
            try {
              await load();
              setContactID(contact?.contactVerifiedStatus === "Verified" ? contact._id : "");
              if (contact?.contactVerifiedStatus !== "Verified") setMessage(contact?.verificationEmailError || "Contact saved. Awaiting email verification before it can be selected for an agreement.");
            } catch (e) { setError(e.message); }
          }}
        />
      )}
    </div>
  );
}
